/* globals ApplePaySession */
import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import ProgressLoader from 'components/essentials/ProgressLoader/ProgressLoader';
import classNames from 'classnames';

import ShippingInfoBlock from '../ShippingInfoBlock';

const ApplePayOption = ({
  checkout,
  showError,
  grandTotal,
  placeOrderLoading,
  placeOrder,
  applePayInstance,
}) => {
  const [loading, setLoading] = useState(false);
  const applePayInstanceRef = useRef(applePayInstance);

  const getBraintreeNonce = () => {
    let errors;
    applePayInstanceRef.current = applePayInstance;
    const paymentRequest = applePayInstanceRef.current.createPaymentRequest({
      total: {
        label: 'Joybird',
        amount: parseFloat(grandTotal).toFixed(2),
      },

      // We recommend collecting billing address information, at minimum
      // billing zip code, and passing that billing zip code with
      // all Apple Pay transactions as a best practice.
      requiredBillingContactFields: ['postalAddress'],
    });
    setLoading(true);
    const session = new ApplePaySession(3, paymentRequest);

    session.onvalidatemerchant = event => {
      applePayInstanceRef.current
        .performValidation({
          validationURL: event.validationURL,
          displayName: 'Joybird',
        })
        .then(merchantSession => {
          session.completeMerchantValidation(merchantSession);
        })
        .catch(validationErr => {
          // You should show an error to the user, e.g. 'Apple Pay failed to load.'
          setLoading(false);
          errors = [{ message: 'Error validating merchant:', validationErr }];
          session.abort();
          return showError(errors);
        });
    };

    session.onpaymentauthorized = event => {
      applePayInstanceRef.current
        .tokenize({
          token: event.payment.token,
        })
        .then(payload => {
          // Send payload.nonce to your server.
          const token = payload.nonce;
          // After you have transacted with the payload.nonce,
          // call `completePayment` to dismiss the Apple Pay sheet.
          setLoading(false);
          session.completePayment(ApplePaySession.STATUS_SUCCESS);
          placeOrder(token);
        })
        .catch(tokenizeErr => {
          errors = [{ message: 'Error tokenizing Apple Pay:', tokenizeErr }];
          setLoading(false);
          session.completePayment(ApplePaySession.STATUS_FAILURE);
          return showError(errors);
        });
    };

    session.begin();
  };

  const uiLoading = loading || placeOrderLoading;

  return (
    <div className="max-[1200px]:pt-8">
      <div className="flex flex-wrap flex-row items-stretch justify-start">
        <button
          type="submit"
          disabled={uiLoading}
          data-xc="place-order-apple-pay-btn"
          className={classNames(
            'inline-block font-bold text-white text-base uppercase p-[1.1rem]',
            'hover:bg-brand',
            'cta apple-btn [background:#585858] text-white normal-case h-[3.75rem] w-full m-0 rounded-none max-[450px]:mt-4 focus:bg-gray',
            {
              'cursor-not-allowed': uiLoading,
            },
            '[&_span]:font-sans [&_span]:font-normal [&_span]:text-[0.875rem]',
            '[&.outline]:bg-gray-light5 [&.outline]:text-[#a5a7a9] [&.outline]:cursor-not-allowed'
          )}
          onClick={getBraintreeNonce}
          style={{
            '-webkit-appearance': '-apple-pay-button',
            '-apple-pay-button-type': 'check-out',
            '-apple-pay-button-style': 'black',
            width: '20.25rem',
            padding: '20px',
            'margin-bottom': '20px',
            'border-radius': '4px',
          }}
        >
          {uiLoading ? <ProgressLoader /> : ''}
        </button>
      </div>
      <div className="flex flex-wrap flex-row items-stretch justify-end">
        {checkout && <ShippingInfoBlock checkout={checkout} />}
      </div>
    </div>
  );
};

ApplePayOption.propTypes = {
  applePayInstance: PropTypes.object,
  checkout: PropTypes.object,
  grandTotal: PropTypes.string,
  placeOrder: PropTypes.func,
  placeOrderLoading: PropTypes.bool,
  showError: PropTypes.func,
};

export default ApplePayOption;
